import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { UserPlus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { CreateUserDialog } from "@/components/users/create-user-dialog"
import { UsersTable } from "@/components/users/users-table"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { AppLayout } from "@/components/app-layout"

export type UserWithProfile = {
  id: string
  created_at?: string
  profiles: {
    first_name: string | null
    last_name: string | null
    email: string | null
  } | null
  user_roles: {
    role: "admin" | "user"
  }[]
}

const UsersContent = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      console.log("Fetching users data...");
      
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          created_at,
          first_name,
          last_name,
          email
        `);

      if (profilesError) {
        console.error("Error fetching profiles:", profilesError);
        toast.error("Erreur lors du chargement des profils");
        throw profilesError;
      }

      console.log("Fetched profiles:", profiles);

      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");

      if (rolesError) {
        console.error("Error fetching roles:", rolesError);
        toast.error("Erreur lors du chargement des rôles");
        throw rolesError;
      }

      console.log("Fetched roles:", roles);

      const transformedUsers = profiles.map((profile) => {
        const userRoles = roles.filter((role) => role.user_id === profile.id);
        
        return {
          id: profile.id,
          created_at: profile.created_at,
          profiles: {
            first_name: profile.first_name,
            last_name: profile.last_name,
            email: profile.email,
          },
          user_roles: userRoles.map(role => ({ role: role.role })),
        };
      }) as UserWithProfile[];

      console.log("Transformed users data:", transformedUsers);
      return transformedUsers;
    },
  });

  // Wrapper function to make refetch return Promise<void>
  const handleUserUpdated = async () => {
    console.log("Starting data refresh in Users component...");
    const result = await refetch();
    console.log("Refetch result:", result);
    console.log("Data refresh completed in Users component");
  };

  const actions = (
    <Button onClick={() => setIsCreateDialogOpen(true)}>
      <UserPlus className="mr-2 h-4 w-4" />
      Nouvel utilisateur
    </Button>
  )

  return (
    <AppLayout 
      title="Utilisateurs" 
      actions={actions}
    >
      <UsersTable 
        users={users ?? []} 
        isLoading={isLoading} 
        onUserUpdated={handleUserUpdated} 
      />

      <CreateUserDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onUserCreated={refetch}
      />
    </AppLayout>
  )
}

export default function Users() {
  return <UsersContent />
}
